import { Button } from '@common/components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import cx from 'classnames';
import selectClasses from './Select.module.scss';
import { InputError, InputLabel } from '../Input/Input';

// Custom ValueContainer for multi-select to show "N selected"
function MultiValueContainer(props) {
  const { t } = useTranslation('_common\\Select');

  const {
    getValue,
    options,
    selectProps: { setMenuIsOpen },
  } = props;
  const selected = getValue();

  let label;
  if (selected.length === 0) {
    label = t('nSelected_none');
  } else if (selected.length === options.length) {
    label = t('all');
  } else {
    label = t('nSelected', { count: selected.length });
  }

  return (
    <components.ValueContainer {...props}>
      <button
        className={selectClasses.selectValueContainerButton}
        onMouseDown={() => setMenuIsOpen((v) => !v)}
      >
        {label}
      </button>
    </components.ValueContainer>
  );
}

// Custom MenuList for multi-select to add "Select all" and "Deselect all"
function MultiValueMenuList(props) {
  const { t } = useTranslation('_common\\Select');
  const { children, selectProps, options, getValue, setValue } = props;

  const selected = getValue();
  const allSelected = selected.length === options.length;

  const handleSelectAll = (e) => {
    e.preventDefault();
    setValue(options);
    if (selectProps.onChange) {
      selectProps.onChange(options);
    }
  };

  const handleDeselectAll = (e) => {
    e.preventDefault();
    setValue([]);
    if (selectProps.onChange) {
      selectProps.onChange([]);
    }
  };

  const handleToggleSelectAll = (e) => {
    if (allSelected) {
      handleDeselectAll(e);
    } else {
      handleSelectAll(e);
    }
  };

  return (
    <components.MenuList {...props}>
      <div className={selectClasses.selectMenuList}>
        <Button
          color="accent"
          type="button"
          onClick={handleToggleSelectAll}
          className={selectClasses.selectMenuListButton}
        >
          {allSelected
            ? t('deselectAll', 'Deselect all')
            : t('selectAll', 'Select all')}
        </Button>
      </div>
      {children}
    </components.MenuList>
  );
}

// Custom Control for multi-select to open menu on click
function MultiValueControl(props) {
  const { setMenuIsOpen } = props.selectProps;
  return (
    <components.Control
      {...props}
      onMouseDown={(e) => {
        setMenuIsOpen(true);
        if (props.innerProps && props.innerProps.onMouseDown) {
          props.innerProps.onMouseDown(e);
        }
      }}
    />
  );
}

export default function RSelect({
  placeholder,
  name,
  className,
  containerClassName,
  data,
  val,
  isDisabled,
  onChange,
  isMultiple,
  error,
}) {
  const [value, setValue] = useState(val || isMultiple ? [] : '');
  const [dataState, setDataState] = useState(data || []);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const cs = cx(selectClasses.select, className);

  useEffect(() => {
    setDataState(data);
  }, [data]);

  useEffect(() => {
    setValue(val);
  }, [val]);

  const handleChange = (event) => {
    setValue(event);
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <div className={cx(selectClasses.selectContainer, containerClassName)}>
      {placeholder ? (
        <InputLabel name={name || ''}>{placeholder}</InputLabel>
      ) : null}
      <Select
        className={cs}
        defaultValue={null}
        onChange={handleChange}
        name={name}
        value={value}
        options={dataState}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isMulti={isMultiple}
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        components={{
          IndicatorSeparator: () => null,
          ...(isMultiple && {
            ValueContainer: MultiValueContainer,
            MenuList: MultiValueMenuList,
            Control: MultiValueControl,
          }),
        }}
        theme={(theme) => ({
          ...theme,
          borderRadius: '3px',
          colors: {
            ...theme.colors,
            primary25: '#dbdbdb',
            primary: '#57BC8F',
            neutral20: '#acacac',
          },
        })}
        styles={{
          control: (provided) => ({
            ...provided,
            paddingTop: 2,
            paddingBottom: 1,
          }),
          input: (provided) => ({
            ...provided,
            padding: 0,
            margin: 0,
          }),
        }}
        // Pass setMenuIsOpen to custom components via selectProps
        setMenuIsOpen={setMenuIsOpen}
      />
      {error && <InputError>{error}</InputError>}
    </div>
  );
}

RSelect.propTypes = {
  placeholder: PropTypes.string,
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  val: PropTypes.any,
  isDisabled: PropTypes.bool,
  isMultiple: PropTypes.bool,
  error: PropTypes.string,
};

MultiValueContainer.propTypes = {
  getValue: PropTypes.func.isRequired,
  selectProps: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
};

MultiValueMenuList.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  getValue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

MultiValueControl.propTypes = {
  ...components.Control.propTypes,
  selectProps: PropTypes.object.isRequired,
};
